import { createPortal } from 'react-dom'
import { AnimatePresence, LayoutGroup, motion } from 'motion/react'

import { animationProps, Wrapper } from '../Modal/ModalFrame'
import { modalRoot } from '../Modal/modalRoot'

import styles from './ModalStack.module.css'
import { useModalStackStore as store } from './store'

/**
 * ### ModalStack is a component that renders a stack of modals.
 *
 * This is basically the way that modals _should_ be rendered. But currently not all modals are implemented using this approach.
 *
 * - It is used to render modals in a stack, so that the modals are rendered in the order they are added.
 * - It is also used to render the modals in the correct order, so that the modals are rendered in the correct order.
 * - Modals should be rendered in a portal, so they are rendered in a separate DOM element. This should naturally have the highest z-index.
 * - ModalStack does not render the modals, it only returns the modals. Creating the portal is the responsibility of the parent component.
 * - Modals are also rendered in a different React tree, so they are not affected by state changes in the parent component.
 * - Models are added using DOM events, so they are not affected by state changes in the parent component.
 * - Modals are also removed using DOM events, so they are not affected by state changes in the parent component.
 * - Keep in mind that any dependant Providers will need to be available either in the parent component of the ModalStack component or in the ModalStack component itself.
 * - Modals using the `useModal` hook will need to have the `stack` property set, as follows: `<Modal stack>...`.
 *
 * @returns An array of modal components.
 */
export const ModalStack = () =>
    createPortal(
        <LayoutGroup>
            <motion.div
                layout
                className={styles.container}
                data-testid="modal-stack"
            >
                <AnimatePresence>
                    {store((state) => state.modals).map((modal) => (
                        <Wrapper key={modal.id} {...animationProps} layout>
                            {modal.component}
                        </Wrapper>
                    ))}
                </AnimatePresence>
            </motion.div>
        </LayoutGroup>,
        modalRoot(),
    )

export default ModalStack
